import React, { useState, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  TextField,
  Grid,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Snackbar,
  Tooltip,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Search,
  CalendarMonth,
  Room,
  Warning,
  Close,
  ContentCopy,
  Layers,
  QueueMusic,
} from '@mui/icons-material';
import {
  useSchedules,
  useCreateSchedule,
  useUpdateSchedule,
  useDeleteSchedule,
  useSchedule,
  useScheduleGaps,
  useZones,
  useSequences,
  useSchedulingPlaylists,
  useAddScheduleEntry,
  useUpdateScheduleEntry,
  useDeleteScheduleEntry,
  useCloneScheduleDay,
} from '../../hooks/useScheduling';
import type { Schedule, ScheduleEntry } from '../../api/types';
import { SectionTabs } from '../../components/SectionTabs';
import { SCHEDULING_TABS, SCHEDULING_ACCENT_COLOR } from './schedulingTabs';
import { schedulingHelp } from '../../components/PageHelp/helpContent';
import { useOrgFilter } from '../../contexts/OrganizationContext';

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const DAY_ABBR = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const SchedulesPage: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState<Schedule | null>(null);
  const [selectedScheduleId, setSelectedScheduleId] = useState<string | null>(null);

  // Use global organization filter
  const orgFilter = useOrgFilter();

  // Fetch zones and schedules filtered by organization
  const { data: zones } = useZones({ organization_id: orgFilter });
  const { data: schedules, isLoading, error } = useSchedules({ organization_id: orgFilter });

  const createMutation = useCreateSchedule();
  const updateMutation = useUpdateSchedule();
  const deleteMutation = useDeleteSchedule();

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    zone_id: '',
    is_active: true,
    is_default: false,
  });

  const handleOpenCreate = () => {
    setFormData({
      name: '',
      description: '',
      zone_id: zones?.[0]?.id || '',
      is_active: true,
      is_default: false,
    });
    setEditingSchedule(null);
    setCreateDialogOpen(true);
  };

  const handleOpenEdit = (schedule: Schedule) => {
    setFormData({
      name: schedule.name,
      description: schedule.description || '',
      zone_id: schedule.zone_id,
      is_active: schedule.is_active,
      is_default: schedule.is_default,
    });
    setEditingSchedule(schedule);
    setCreateDialogOpen(true);
  };

  const handleSubmit = async () => {
    try {
      if (editingSchedule) {
        await updateMutation.mutateAsync({
          id: editingSchedule.id,
          data: formData,
        });
      } else {
        await createMutation.mutateAsync(formData);
      }
      setCreateDialogOpen(false);
    } catch (err) {
      console.error('Failed to save schedule:', err);
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this schedule?')) {
      await deleteMutation.mutateAsync(id);
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        Failed to load schedules: {(error as Error)?.message}
      </Alert>
    );
  }

  return (
    <Box>
      <SectionTabs
        title="Scheduling"
        tabs={SCHEDULING_TABS}
        accentColor={SCHEDULING_ACCENT_COLOR}
        help={schedulingHelp.schedules}
        actionButton={{
          label: 'New Schedule',
          icon: <Add />,
          onClick: handleOpenCreate,
        }}
      />

      {/* Search */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search schedules..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
          }}
          size="small"
        />
      </Paper>

      {/* Schedules Grid */}
      <Grid container spacing={2}>
        {schedules?.map((schedule) => (
          <Grid item xs={12} sm={6} md={4} key={schedule.id}>
            <Card
              sx={{
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: 3,
                },
                opacity: schedule.is_active ? 1 : 0.6,
              }}
              onClick={() => setSelectedScheduleId(schedule.id)}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <CalendarMonth sx={{ color: '#f97316' }} />
                  <Typography variant="h6" sx={{ fontWeight: 600, flex: 1 }}>
                    {schedule.name}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenEdit(schedule);
                    }}
                  >
                    <Edit fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(schedule.id);
                    }}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Room sx={{ fontSize: 16, color: 'text.secondary' }} />
                  <Typography variant="body2" color="text.secondary">
                    {schedule.zone_name} - {schedule.site_name}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {schedule.is_active ? (
                    <Chip size="small" label="Active" sx={{ bgcolor: '#10b98120', color: '#10b981' }} />
                  ) : (
                    <Chip size="small" label="Inactive" variant="outlined" />
                  )}
                  {schedule.is_default && (
                    <Chip size="small" label="Default" sx={{ bgcolor: '#f9731620', color: '#f97316' }} />
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}

        {schedules?.length === 0 && (
          <Grid item xs={12}>
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <CalendarMonth sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                No schedules yet
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Create schedules to program content for your zones
              </Typography>
              <Button variant="contained" onClick={handleOpenCreate}>
                Create Schedule
              </Button>
            </Paper>
          </Grid>
        )}
      </Grid>

      {/* Create/Edit Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingSchedule ? 'Edit Schedule' : 'Create New Schedule'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
              required
            />

            <TextField
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              fullWidth
              multiline
              rows={2}
            />

            <FormControl fullWidth required>
              <InputLabel>Zone</InputLabel>
              <Select
                value={formData.zone_id}
                label="Zone"
                onChange={(e) => setFormData({ ...formData, zone_id: e.target.value })}
              >
                {zones?.map((zone) => (
                  <MenuItem key={zone.id} value={zone.id}>
                    {zone.name} - {zone.site_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                />
              }
              label="Active"
            />

            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_default}
                  onChange={(e) => setFormData({ ...formData, is_default: e.target.checked })}
                />
              }
              label="Default schedule for this zone"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!formData.name || !formData.zone_id || createMutation.isPending || updateMutation.isPending}
          >
            {editingSchedule ? 'Save Changes' : 'Create Schedule'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Schedule Detail Dialog */}
      {selectedScheduleId && (
        <ScheduleDetailDialog
          scheduleId={selectedScheduleId}
          onClose={() => setSelectedScheduleId(null)}
        />
      )}
    </Box>
  );
};

// Hours array for calendar grid
const HOURS = Array.from({ length: 24 }, (_, i) => i);

// Add Entry Dialog Component
const AddEntryDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  scheduleId: string;
  initialDay?: number;
  initialStartHour?: number;
  editingEntry?: ScheduleEntry | null;
}> = ({ open, onClose, scheduleId, initialDay = 0, initialStartHour = 9, editingEntry }) => {
  // Fetch ALL sequences and playlists the user has access to (no org filter)
  const { data: sequences } = useSequences({});
  const { data: playlists } = useSchedulingPlaylists({});

  const addEntryMutation = useAddScheduleEntry();
  const updateEntryMutation = useUpdateScheduleEntry();

  const [formData, setFormData] = useState({
    day_of_week: editingEntry?.day_of_week ?? initialDay,
    start_time: editingEntry?.start_time ?? `${initialStartHour.toString().padStart(2, '0')}:00`,
    end_time: editingEntry?.end_time ?? `${(initialStartHour + 2).toString().padStart(2, '0')}:00`,
    content_type: editingEntry?.content_type ?? 'sequence',
    sequence_id: editingEntry?.sequence_id ?? '',
    playlist_id: editingEntry?.playlist_id ?? '',
    priority: editingEntry?.priority ?? 0,
  });

  // Reset form when dialog opens with new data
  React.useEffect(() => {
    if (open) {
      setFormData({
        day_of_week: editingEntry?.day_of_week ?? initialDay,
        start_time: editingEntry?.start_time ?? `${initialStartHour.toString().padStart(2, '0')}:00`,
        end_time: editingEntry?.end_time ?? `${Math.min(24, initialStartHour + 2).toString().padStart(2, '0')}:00`,
        content_type: editingEntry?.content_type ?? 'sequence',
        sequence_id: editingEntry?.sequence_id ?? '',
        playlist_id: editingEntry?.playlist_id ?? '',
        priority: editingEntry?.priority ?? 0,
      });
    }
  }, [open, editingEntry, initialDay, initialStartHour]);

  const handleSubmit = async () => {
    try {
      const data = {
        day_of_week: formData.day_of_week,
        start_time: formData.start_time,
        end_time: formData.end_time,
        content_type: formData.content_type,
        sequence_id: formData.content_type === 'sequence' ? formData.sequence_id : undefined,
        playlist_id: formData.content_type === 'playlist' ? formData.playlist_id : undefined,
        priority: formData.priority,
      };

      if (editingEntry) {
        await updateEntryMutation.mutateAsync({
          scheduleId,
          entryId: editingEntry.id,
          data,
        });
      } else {
        await addEntryMutation.mutateAsync({ scheduleId, data });
      }
      onClose();
    } catch (err) {
      console.error('Failed to save entry:', err);
    }
  };

  const isValid = formData.start_time < formData.end_time &&
    ((formData.content_type === 'sequence' && formData.sequence_id) ||
     (formData.content_type === 'playlist' && formData.playlist_id));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{editingEntry ? 'Edit Entry' : 'Add Schedule Entry'}</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <FormControl fullWidth>
            <InputLabel>Day</InputLabel>
            <Select
              value={formData.day_of_week}
              label="Day"
              onChange={(e) => setFormData({ ...formData, day_of_week: e.target.value as number })}
            >
              {DAY_NAMES.map((day, index) => (
                <MenuItem key={day} value={index}>{day}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Start Time"
              type="time"
              value={formData.start_time}
              onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="End Time"
              type="time"
              value={formData.end_time}
              onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
              fullWidth
              InputLabelProps={{ shrink: true }}
              error={formData.end_time <= formData.start_time}
              helperText={formData.end_time <= formData.start_time ? 'End time must be after start time' : ''}
            />
          </Box>

          <FormControl fullWidth>
            <InputLabel>Content Type</InputLabel>
            <Select
              value={formData.content_type}
              label="Content Type"
              onChange={(e) => setFormData({
                ...formData,
                content_type: e.target.value as 'playlist' | 'sequence' | 'channel',
                sequence_id: '',
                playlist_id: '',
              })}
            >
              <MenuItem value="sequence">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Layers fontSize="small" /> Sequence
                </Box>
              </MenuItem>
              <MenuItem value="playlist">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <QueueMusic fontSize="small" /> Playlist
                </Box>
              </MenuItem>
            </Select>
          </FormControl>

          {formData.content_type === 'sequence' && (
            <FormControl fullWidth required>
              <InputLabel>Sequence</InputLabel>
              <Select
                value={formData.sequence_id}
                label="Sequence"
                onChange={(e) => setFormData({ ...formData, sequence_id: e.target.value })}
              >
                {sequences?.map((seq) => (
                  <MenuItem key={seq.id} value={seq.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: seq.color }} />
                      {seq.name}
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                        {seq.segment_count} segments
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {formData.content_type === 'playlist' && (
            <FormControl fullWidth required>
              <InputLabel>Playlist</InputLabel>
              <Select
                value={formData.playlist_id}
                label="Playlist"
                onChange={(e) => setFormData({ ...formData, playlist_id: e.target.value })}
              >
                {playlists?.map((pl) => (
                  <MenuItem key={pl.id} value={pl.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: pl.color }} />
                      {pl.name}
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                        {pl.track_count} tracks
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <TextField
            label="Priority"
            type="number"
            value={formData.priority}
            onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) || 0 })}
            helperText="Higher priority entries override lower ones"
            InputProps={{ inputProps: { min: 0, max: 10 } }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!isValid || addEntryMutation.isPending || updateEntryMutation.isPending}
        >
          {editingEntry ? 'Save Changes' : 'Add Entry'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Clone Day Dialog Component
const CloneDayDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  scheduleId: string;
  entriesByDay: Record<number, any[]>;
}> = ({ open, onClose, scheduleId, entriesByDay }) => {
  const [sourceDay, setSourceDay] = useState(0);
  const [targetDays, setTargetDays] = useState<number[]>([]);

  const cloneMutation = useCloneScheduleDay();

  const handleToggleDay = (day: number) => {
    if (day === sourceDay) return;
    setTargetDays(prev =>
      prev.includes(day)
        ? prev.filter(d => d !== day)
        : [...prev, day]
    );
  };

  const handleClone = async () => {
    try {
      await cloneMutation.mutateAsync({
        scheduleId,
        sourceDay,
        targetDays,
      });
      onClose();
    } catch (err) {
      console.error('Failed to clone day:', err);
    }
  };

  // Find days with entries
  const daysWithEntries = Object.keys(entriesByDay)
    .filter(day => entriesByDay[parseInt(day)]?.length > 0)
    .map(Number);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Clone Day</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
          <FormControl fullWidth>
            <InputLabel>Source Day (copy from)</InputLabel>
            <Select
              value={sourceDay}
              label="Source Day (copy from)"
              onChange={(e) => {
                setSourceDay(e.target.value as number);
                setTargetDays([]);
              }}
            >
              {DAY_NAMES.map((day, index) => (
                <MenuItem
                  key={day}
                  value={index}
                  disabled={!daysWithEntries.includes(index)}
                >
                  {day} {entriesByDay[index]?.length ? `(${entriesByDay[index].length} entries)` : '(no entries)'}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Target Days (copy to)
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {DAY_NAMES.map((day, index) => (
                <Chip
                  key={day}
                  label={day}
                  onClick={() => handleToggleDay(index)}
                  color={targetDays.includes(index) ? 'primary' : 'default'}
                  variant={targetDays.includes(index) ? 'filled' : 'outlined'}
                  disabled={index === sourceDay}
                  sx={{ cursor: index === sourceDay ? 'not-allowed' : 'pointer' }}
                />
              ))}
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Warning: Existing entries on target days will be replaced
            </Typography>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleClone}
          variant="contained"
          disabled={targetDays.length === 0 || cloneMutation.isPending}
        >
          Clone to {targetDays.length} day{targetDays.length !== 1 ? 's' : ''}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Schedule Detail Dialog Component with Calendar
const ScheduleDetailDialog: React.FC<{
  scheduleId: string;
  onClose: () => void;
}> = ({ scheduleId, onClose }) => {
  const { data: schedule, isLoading } = useSchedule(scheduleId);
  const { data: gaps } = useScheduleGaps(scheduleId);
  const deleteEntryMutation = useDeleteScheduleEntry();

  const [addEntryOpen, setAddEntryOpen] = useState(false);
  const [cloneDayOpen, setCloneDayOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState<ScheduleEntry | null>(null);
  const [selectedDay, setSelectedDay] = useState<number | null>(null);
  const [selectedHour, setSelectedHour] = useState<number | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });

  // Organize entries by day
  const entriesByDay = useMemo(() => {
    const result: Record<number, any[]> = {};
    for (let i = 0; i < 7; i++) result[i] = [];
    schedule?.entries?.forEach((entry: any) => {
      result[entry.day_of_week]?.push(entry);
    });
    return result;
  }, [schedule?.entries]);

  const handleCellClick = (day: number, hour: number) => {
    setSelectedDay(day);
    setSelectedHour(hour);
    setEditingEntry(null);
    setAddEntryOpen(true);
  };

  const handleEditEntry = (entry: ScheduleEntry) => {
    setEditingEntry(entry);
    setAddEntryOpen(true);
  };

  const handleDeleteEntry = async (entryId: string) => {
    if (window.confirm('Delete this entry?')) {
      try {
        await deleteEntryMutation.mutateAsync({ scheduleId, entryId });
        setSnackbar({ open: true, message: 'Entry deleted' });
      } catch (err) {
        setSnackbar({ open: true, message: 'Failed to delete entry' });
      }
    }
  };

  if (isLoading || !schedule) {
    return (
      <Dialog open onClose={onClose} maxWidth="xl" fullWidth>
        <DialogContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  const formatTime = (time: string) => {
    const [hours, minutes] = time.split(':');
    const h = parseInt(hours, 10);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const hour12 = h % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  // Calculate entry position and height for calendar visualization
  const getEntryStyle = (entry: any) => {
    const [startH, startM] = entry.start_time.split(':').map(Number);
    const [endH, endM] = entry.end_time.split(':').map(Number);
    const startMinutes = startH * 60 + startM;
    const endMinutes = endH * 60 + endM;
    const durationMinutes = endMinutes - startMinutes;

    return {
      top: `${(startMinutes / (24 * 60)) * 100}%`,
      height: `${(durationMinutes / (24 * 60)) * 100}%`,
    };
  };

  const totalEntries = schedule.entries?.length || 0;
  const gapCount = gaps?.length || 0;

  return (
    <>
      <Dialog open onClose={onClose} maxWidth="xl" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CalendarMonth sx={{ color: '#f97316' }} />
            <Typography variant="h6" sx={{ flex: 1 }}>{schedule.name}</Typography>
            <Chip label={schedule.zone_name} size="small" icon={<Room />} />
            <Chip label={`${totalEntries} entries`} size="small" variant="outlined" />
            {gapCount > 0 && (
              <Chip
                label={`${gapCount} gaps`}
                size="small"
                color="warning"
                icon={<Warning />}
              />
            )}
            <IconButton onClick={onClose}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {/* Toolbar */}
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => {
                setEditingEntry(null);
                setSelectedDay(0);
                setSelectedHour(9);
                setAddEntryOpen(true);
              }}
              size="small"
            >
              Add Entry
            </Button>
            <Button
              variant="outlined"
              startIcon={<ContentCopy />}
              onClick={() => setCloneDayOpen(true)}
              size="small"
            >
              Clone Day
            </Button>
            <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', alignSelf: 'center' }}>
              Click on a time slot to add an entry
            </Typography>
          </Box>

          {/* Calendar Grid - scrollable to show all 24 hours */}
          <Box sx={{ display: 'flex', height: 'calc(100vh - 200px)', minHeight: 500, maxHeight: 700, overflow: 'auto' }}>
            {/* Time column */}
            <Box sx={{ width: 60, flexShrink: 0, borderRight: 1, borderColor: 'divider' }}>
              <Box sx={{ height: 40, borderBottom: 1, borderColor: 'divider' }} />
              {HOURS.map((hour) => (
                <Box
                  key={hour}
                  sx={{
                    height: `${100 / 24}%`,
                    borderBottom: 1,
                    borderColor: 'divider',
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'flex-end',
                    pr: 1,
                    pt: 0.25,
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    {hour === 0 ? '12 AM' : hour < 12 ? `${hour} AM` : hour === 12 ? '12 PM' : `${hour - 12} PM`}
                  </Typography>
                </Box>
              ))}
            </Box>

            {/* Day columns */}
            <Box sx={{ flex: 1, display: 'flex', overflow: 'auto' }}>
              {DAY_ABBR.map((day, dayIndex) => (
                <Box
                  key={day}
                  sx={{
                    flex: 1,
                    minWidth: 120,
                    borderRight: 1,
                    borderColor: 'divider',
                    position: 'relative',
                  }}
                >
                  {/* Day header */}
                  <Box
                    sx={{
                      height: 40,
                      borderBottom: 1,
                      borderColor: 'divider',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: entriesByDay[dayIndex]?.length ? 'action.selected' : 'background.default',
                      position: 'sticky',
                      top: 0,
                      zIndex: 2,
                    }}
                  >
                    <Typography variant="subtitle2">
                      {day}
                      {entriesByDay[dayIndex]?.length > 0 && (
                        <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                          ({entriesByDay[dayIndex].length})
                        </Typography>
                      )}
                    </Typography>
                  </Box>

                  {/* Hour slots - clickable background */}
                  <Box sx={{ position: 'relative', height: 'calc(100% - 40px)' }}>
                    {HOURS.map((hour) => (
                      <Box
                        key={hour}
                        onClick={() => handleCellClick(dayIndex, hour)}
                        sx={{
                          height: `${100 / 24}%`,
                          borderBottom: 1,
                          borderColor: hour % 6 === 5 ? 'divider' : 'action.hover',
                          cursor: 'pointer',
                          '&:hover': {
                            bgcolor: 'action.hover',
                          },
                        }}
                      />
                    ))}

                    {/* Entries */}
                    {entriesByDay[dayIndex]?.map((entry: any) => {
                      const style = getEntryStyle(entry);
                      const color = entry.sequence_color || entry.playlist_color || entry.channel_color || '#666';

                      return (
                        <Tooltip
                          key={entry.id}
                          title={
                            <Box>
                              <Typography variant="body2">
                                {entry.sequence_name || entry.playlist_name || entry.channel_name}
                              </Typography>
                              <Typography variant="caption">
                                {formatTime(entry.start_time)} - {formatTime(entry.end_time)}
                              </Typography>
                            </Box>
                          }
                        >
                          <Box
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditEntry(entry);
                            }}
                            sx={{
                              position: 'absolute',
                              left: 2,
                              right: 2,
                              top: style.top,
                              height: style.height,
                              minHeight: 20,
                              bgcolor: color,
                              borderRadius: 0.5,
                              p: 0.5,
                              overflow: 'hidden',
                              cursor: 'pointer',
                              display: 'flex',
                              flexDirection: 'column',
                              '&:hover': {
                                opacity: 0.9,
                                '& .entry-actions': {
                                  opacity: 1,
                                },
                              },
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                color: 'white',
                                fontWeight: 600,
                                fontSize: '0.65rem',
                                lineHeight: 1.2,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {entry.sequence_name || entry.playlist_name || entry.channel_name}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                color: 'rgba(255,255,255,0.8)',
                                fontSize: '0.6rem',
                                lineHeight: 1.2,
                              }}
                            >
                              {formatTime(entry.start_time)}
                            </Typography>
                            <Box
                              className="entry-actions"
                              sx={{
                                position: 'absolute',
                                top: 2,
                                right: 2,
                                opacity: 0,
                                transition: 'opacity 0.2s',
                              }}
                            >
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteEntry(entry.id);
                                }}
                                sx={{
                                  bgcolor: 'rgba(255,255,255,0.9)',
                                  p: 0.25,
                                  '&:hover': { bgcolor: 'white' },
                                }}
                              >
                                <Delete sx={{ fontSize: 14, color: 'error.main' }} />
                              </IconButton>
                            </Box>
                          </Box>
                        </Tooltip>
                      );
                    })}
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Entry Dialog */}
      <AddEntryDialog
        open={addEntryOpen}
        onClose={() => {
          setAddEntryOpen(false);
          setEditingEntry(null);
        }}
        scheduleId={scheduleId}
        initialDay={selectedDay ?? 0}
        initialStartHour={selectedHour ?? 9}
        editingEntry={editingEntry}
      />

      {/* Clone Day Dialog */}
      <CloneDayDialog
        open={cloneDayOpen}
        onClose={() => setCloneDayOpen(false)}
        scheduleId={scheduleId}
        entriesByDay={entriesByDay}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
      />
    </>
  );
};

export default SchedulesPage;
